/**
 * WaterBodyBlock — proximity scan of nearby ponds/lakes/rivers/streams.
 *
 * Rendered on the LocalityReportCard for PLOT/LAND properties only. Fetches
 * `/api/properties/{id}/waterbody-report` and shows a colour-coded verdict
 * (critical/warning/clear) plus the top-5 features with type + distance.
 * Falls back gracefully when Overpass is down.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Droplets, Waves, AlertTriangle, ShieldCheck, RefreshCw, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VERDICT_STYLE = {
  critical: { bg: 'bg-rose-50',   border: 'border-rose-300',   text: 'text-rose-900',   dot: 'bg-rose-600',    label: 'AT PLOT EDGE' },
  warning:  { bg: 'bg-amber-50',  border: 'border-amber-300',  text: 'text-amber-900',  dot: 'bg-amber-600',   label: 'VERY CLOSE' },
  clear:    { bg: 'bg-sky-50',    border: 'border-sky-300',    text: 'text-sky-900',    dot: 'bg-sky-600',     label: 'SAFE' },
  info:     { bg: 'bg-slate-50',  border: 'border-slate-300',  text: 'text-slate-800',  dot: 'bg-slate-500',   label: 'INFO' },
};

const ICON_FOR_TYPE = (t) => {
  const l = (t || '').toLowerCase();
  if (l.includes('river') || l.includes('stream') || l.includes('canal') || l.includes('drain')) return Waves;
  return Droplets;
};

export const WaterBodyBlock = ({ propertyId, preloaded, onRetry }) => {
  const [state, setState] = useState(
    preloaded && preloaded.data
      ? { loading: false, data: preloaded.data, error: null }
      : preloaded && preloaded.loading
      ? { loading: true, data: null, error: null }
      : { loading: true, data: null, error: null },
  );

  // Sync when parent-supplied state changes
  useEffect(() => {
    if (!preloaded) return;
    setState({
      loading: !!preloaded.loading,
      data: preloaded.data || null,
      error: preloaded.error || null,
    });
  }, [preloaded]);

  const fetchReport = async (refresh = false) => {
    if (onRetry) return onRetry({ refresh });
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/properties/${propertyId}/waterbody-report${refresh ? '?refresh=true' : ''}`,
        { timeout: 45000 },
      );
      setState({ loading: false, data: res.data, error: null });
    } catch (err) {
      setState({ loading: false, data: null, error: err?.response?.data?.detail || 'Water-body scan failed.' });
    }
  };

  useEffect(() => {
    if (preloaded) return; // parent controls fetching
    if (propertyId) fetchReport(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, preloaded]);

  if (state.loading) {
    return (
      <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50/60 p-4 flex items-center gap-2" data-testid="waterbody-loading">
        <Loader2 size={16} className="animate-spin text-sky-700" />
        <span className="text-sm text-sky-900">Scanning nearby ponds, streams and rivers…</span>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="waterbody-error">
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} className="text-amber-600" />
          Water body scan unavailable right now.
          <button type="button" onClick={() => fetchReport(true)} className="ml-auto text-xs font-black text-sky-700 hover:text-sky-900 inline-flex items-center gap-1">
            <RefreshCw size={11} /> Retry
          </button>
        </div>
      </div>
    );
  }

  const d = state.data;
  if (!d) return null;
  const verdictKey = d.verdict || 'info';
  const v = VERDICT_STYLE[verdictKey] || VERDICT_STYLE.info;

  return (
    <div
      className={`mt-4 rounded-2xl border-2 ${v.border} ${v.bg} p-4 shadow-sm`}
      data-testid="waterbody-block"
      data-verdict={verdictKey}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className={`w-10 h-10 rounded-xl grid place-items-center text-white shadow ${v.dot}`}>
          <Droplets size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 rounded-full px-2 py-0.5 border ${v.border} ${v.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${v.dot}`} /> Water bodies · {v.label}
            </div>
            {d.degraded && (
              <span className="text-[9px] font-black uppercase tracking-widest bg-amber-100 border border-amber-300 text-amber-800 rounded-full px-2 py-0.5">
                OSM busy · showing safe fallback
              </span>
            )}
            <button
              type="button"
              onClick={() => fetchReport(true)}
              className={`ml-auto text-[10px] font-black inline-flex items-center gap-1 ${v.text} hover:opacity-80`}
              data-testid="waterbody-refresh"
              title="Re-scan OSM now"
            >
              <RefreshCw size={11} /> Re-scan
            </button>
          </div>
          <h4 className={`text-base font-black mt-1 ${v.text}`}>{d.verdict_label || 'Water body proximity'}</h4>
          <p className={`text-xs mt-1 ${v.text} opacity-90 leading-relaxed`}>{d.verdict_notes}</p>
        </div>
      </div>

      {/* Feature list */}
      {d.features && d.features.length > 0 && (
        <div className="mt-3 grid gap-1.5">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            {d.features.length} water bod{d.features.length === 1 ? 'y' : 'ies'} within 500 m
          </div>
          <ul className="grid gap-1">
            {d.features.slice(0, 6).map((f) => {
              const Icon = ICON_FOR_TYPE(f.type);
              const badge = f.distance_m <= 30 ? 'bg-rose-100 text-rose-800 border-rose-200' :
                            f.distance_m <= 120 ? 'bg-amber-100 text-amber-800 border-amber-200' :
                            'bg-sky-100 text-sky-800 border-sky-200';
              return (
                <li key={f.id} className="flex items-center gap-2 text-xs bg-white/70 backdrop-blur rounded-lg px-2.5 py-1.5 border border-slate-200">
                  <Icon size={13} className="text-sky-700 shrink-0" />
                  <span className="font-bold text-slate-900 truncate">
                    {f.name ? `${f.name} · ` : ''}{f.type}
                  </span>
                  <span className={`ml-auto text-[10px] font-black uppercase tracking-widest border rounded-full px-2 py-0.5 shrink-0 ${badge}`}>
                    {Math.round(f.distance_m)} m
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mt-3 text-[10px] text-slate-500 flex items-center gap-1">
        <ShieldCheck size={11} className="text-emerald-600" />
        Data sourced live from OpenStreetMap — no third-party charges.
      </div>
    </div>
  );
};

export default WaterBodyBlock;
